/**
 * EventBanner Component
 *
 * Professional banner component for displaying event notifications
 * with countdown timer and dismiss functionality.
 *
 * Features:
 * - Countdown timer (days/hours remaining)
 * - Dismissible with localStorage persistence
 * - Responsive design
 * - Professional styling with brand colors
 * - Click to view details (opens modal)
 */

import { getCountdownText, getDaysUntilEvent } from "@/config/events.config";
import { Event } from "@/types/events.types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCalendarDay, FaClock, FaEye, FaTimes } from "react-icons/fa";

interface EventBannerProps {
  event: Event;
  onDismiss: () => void;
  onViewDetails?: () => void;
}

export const EventBanner: React.FC<EventBannerProps> = ({
  event,
  onDismiss,
  onViewDetails,
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState<string>("");
  const [daysRemaining, setDaysRemaining] = useState<number>(0);

  // Update countdown every minute
  useEffect(() => {
    const updateCountdown = () => {
      const days = getDaysUntilEvent(event.endDate);
      const countdownText = getCountdownText(event.endDate);
      setDaysRemaining(days);
      setCountdown(countdownText);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [event.endDate]);

  // Don't render if event has passed
  if (daysRemaining < 0) {
    return null;
  }

  const isToday = daysRemaining === 0;

  return (
    <div className="event-banner">
      <div className="event-banner-container">
        {/* Left Section: Event Image */}
        <div className="event-banner-image-section">
          {event.bannerImageUrl && (
            <img
              src={event.bannerImageUrl}
              alt={event.title}
              className="event-banner-image"
              onClick={onViewDetails}
            />
          )}
        </div>

        {/* Center Section: Event Details */}
        <div className="event-banner-content">
          <div className="event-banner-title-section">
            <h3 className="event-banner-title">{event.title}</h3>
            {event.description && (
              <p className="event-banner-description">{event.description}</p>
            )}
          </div>

          {/* Countdown Badge */}
          <div className="event-banner-countdown">
            <div
              className={`event-countdown-badge ${
                isToday ? "event-today" : ""
              }`}
            >
              {isToday ? (
                <>
                  <FaCalendarDay className="event-countdown-icon pulse-animation" />
                  <span className="event-countdown-text">
                    {t("events.today", "आज का दिन है!")}
                  </span>
                </>
              ) : (
                <>
                  <FaClock className="event-countdown-icon" />
                  <span className="event-countdown-text">{countdown}</span>
                </>
              )}
            </div>
          </div>

          {/* Action Button */}
          {onViewDetails && (
            <button onClick={onViewDetails} className="event-banner-action-btn">
              <FaEye className="inline-block mr-2" />
              {t("events.viewDetails", "विवरण देखें")}
            </button>
          )}
        </div>

        {/* Right Section: Dismiss Button */}
        {event.dismissible && (
          <button
            onClick={onDismiss}
            className="event-banner-dismiss"
            aria-label={t("events.dismiss", "Dismiss")}
            title={t("events.dismiss", "Dismiss")}
          >
            <FaTimes />
          </button>
        )}
      </div>
    </div>
  );
};

export default EventBanner;
